import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "@/api/auth";
import { Button, Input } from "@/components";
import { useAuthStore } from "@/state/auth.store";
import { toast } from "@/state/toast.store";
import { useLang } from "@/i18n/LanguageContext";

/** Set a new password from a reset OR invitation token (?token=…). */
export function ResetPasswordPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const setSession = useAuthStore((s) => s.setSession);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!token) {
    return (
      <div className="center-col">
        <h1 style={{ marginBottom: 8 }}>{t("Ongeldige link", "Invalid link")}</h1>
        <p style={{ color: "var(--grey-text)", marginBottom: 24 }}>
          {t("Deze link mist een geldige code. Vraag een nieuwe aan.", "This link is missing a valid code. Request a new one.")}
        </p>
        <Link to="/forgot-password" className="btn btn-outline">{t("Nieuwe link aanvragen", "Request a new link")}</Link>
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t("De wachtwoorden komen niet overeen.", "The passwords do not match."));
      return;
    }
    setBusy(true);
    try {
      const res = await resetPassword(token, password);
      setSession(res.token, res.user);
      toast.ok(t("Wachtwoord ingesteld.", "Password set."));
      navigate("/app", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Mislukt", "Failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="center-col">
      <p className="eyebrow">{t("Nieuw wachtwoord", "New password")}</p>
      <h1 style={{ marginBottom: 24 }}>{t("Kies een wachtwoord", "Choose a password")}</h1>
      <form onSubmit={onSubmit}>
        {error && <div className="form-error">{error}</div>}
        <Input label={t("Nieuw wachtwoord", "New password")} name="password" type="password" autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
        <Input label={t("Herhaal wachtwoord", "Repeat password")} name="confirm" type="password" autoComplete="new-password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        <Button type="submit" disabled={busy}>{busy ? t("Bezig…", "Working…") : t("Wachtwoord opslaan", "Save password")}</Button>
      </form>
    </div>
  );
}
